import { useMutation, useQuery, useQueryClient } from 'react-query';
import { findIndex, without } from 'lodash';
import moment from 'moment';
import * as api from './api';
import {
  Chunk,
  StudyPlanDataInterface,
  StudyPlanItem
} from '@magoosh/gre/adaptive_prep_v2/pages/study_plan/types';

// These queries and mutations use React hooks, which can only be called from within a functional
// component. Because of that we need to wrap them in functions like below. To access them in a
// functional component:
//   import { useQueries } from './queries';
//   const q = useQueries();
export const useQueries = () => {
  return {
    dismissStudyPlanRefreshMutation: dismissStudyPlanRefreshMutation(),
    fetchMoreChunks: fetchMoreChunks(),
    hideStudyPlanItemMutation: hideStudyPlanItemMutation(),
    markStudyPlanItemCompleteMutation: markStudyPlanItemCompleteMutation(),
    setAdaptiveHoursPerChunkMutation: setAdaptiveHoursPerChunkMutation(),
    updateStudyPlanMutation: updateStudyPlanMutation(),
    createOrFindStudyPlanMutation: createOrFindStudyPlanMutation(),
    liveClassesWidgetQuery,
    notesQuery,
    segmentDetailQuery,
    studyPlanQuery,
    studyPlanChunksQuery,
    completedStudyPlanItemsQuery,
    presetStudyPlanItemsQuery,
    topicsQuery,
    checkpointsQuery,
    sidebarQuery
  };
};

// Utility function
const chunksWithoutItem = (chunks, item) => {
  const updatedChunks = [];
  chunks.forEach((chunk) => {
    updatedChunks.push({ ...chunk, studyPlanItems: without(chunk.studyPlanItems, item) });
  });
  return updatedChunks;
};

export const checkpointsQuery = (options = {}) => {
  return useQuery('checkpoints', api.fetchCheckpoints, {
    retry: false,
    staleTime: 1000,
    ...options
  });
};

export const liveClassesWidgetQuery = (options = {}) => {
  return useQuery('liveClasses[widget]', () => api.fetchLiveClasses('widget'), {
    retry: false,
    staleTime: 1000,
    ...options
  });
};

export const notesQuery = (sectionName: string, options = {}) => {
  return useQuery(`notes[${sectionName}]`, () => api.fetchNotes(sectionName), {
    retry: false,
    staleTime: 1000,
    ...options
  });
};

export const segmentDetailQuery = (segmentId: number, options = {}) => {
  return useQuery(`segments[${segmentId}]`, () => api.fetchSegment(segmentId), {
    retry: false,
    staleTime: 1000,
    ...options
  });
};

export const sidebarQuery = (options = {}) => {
  return useQuery('sidebar', api.fetchSidebar, {
    retry: false,
    staleTime: 1000,
    ...options
  });
};

export const studyPlanQuery = (options = {}) => {
  return useQuery('studyPlan', api.fetchStudyPlan, {
    retry: false,
    staleTime: 1000,
    ...options
  });
};

export const studyPlanChunksQuery = (chunkArgsFn, options = {}) => {
  return useQuery('studyPlanChunks', () => api.fetchStudyPlanChunks(chunkArgsFn()), {
    retry: false,
    staleTime: 1000,
    ...options
  });
};

export const completedStudyPlanItemsQuery = (options = {}) => {
  return useQuery('completedStudyPlanItems', api.fetchCompletedStudyPlanItems, {
    retry: false,
    staleTime: 1000,
    ...options
  });
};

export const presetStudyPlanItemsQuery = (options = {}) => {
  return useQuery('presetStudyPlanItems', api.fetchPresetStudyPlanItems, {
    retry: false,
    staleTime: 1000,
    ...options
  });
};

export const topicsQuery = (sectionName: string, options = {}) => {
  return useQuery(`topics[${sectionName}]`, () => api.fetchSubsections(sectionName), {
    retry: false,
    staleTime: 1000,
    ...options
  });
};

export const createOrFindStudyPlanMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(api.createOrFindStudyPlan, {
    onMutate: async () => {
      await queryClient.cancelQueries([
        'studyPlan',
        'studyPlanChunks',
        'presetStudyPlanItems'
      ]);

      // A prioritized checkpoint could be responsible for creating the new study plan. Remove
      // it so that the UI feels more responsive.
      const currentData: any = queryClient.getQueryData('checkpoints');
      if (currentData.prioritizedCheckpoint) {
        queryClient.setQueryData('checkpoints', { ...currentData, prioritizedCheckpoint: null });
      }
    },
    onSuccess: (result: any, variables, context) => {
      queryClient.setQueryData('studyPlan', result.studyPlan);
      queryClient.invalidateQueries('checkpoints');
      
      // Using `resetQueries` here so that the UI will go back into the loading state
      if (result.studyPlan.planType == 'adaptive') {
        queryClient.resetQueries('studyPlanChunks');
      } else {
        queryClient.resetQueries('presetStudyPlanItems');
      }
    },
  });
};

export const dismissStudyPlanRefreshMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(api.dismissRefresh, {
    onMutate: async () => {
      await queryClient.cancelQueries('checkpoints');

      const currentData: any = queryClient.getQueryData('checkpoints');
      if (currentData.prioritizedCheckpoint) {
        queryClient.setQueryData('checkpoints', { ...currentData, prioritizedCheckpoint: null });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries('checkpoints');
    }
  });
};

export const fetchMoreChunks = () => {
  const queryClient = useQueryClient();

  return useMutation(api.fetchStudyPlanChunks, {
    onMutate: async (settings) => {
      await queryClient.cancelQueries('studyPlanChunks');
    },
    onSuccess: (result: any, variables, context) => {
      const currentData: any = queryClient.getQueryData('studyPlanChunks');
      queryClient.setQueryData('studyPlanChunks', [...currentData, ...result]);
    }
  });
};

export const hideStudyPlanItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(api.hideStudyPlanItem, {
    onMutate: async ({ item, permanent }) => {
      await queryClient.cancelQueries('studyPlanChunks');

      // Optimistically remove the item from the todo tab
      const currentChunks: Chunk[] = queryClient.getQueryData('studyPlanChunks');
      queryClient.setQueryData('studyPlanChunks', chunksWithoutItem(currentChunks, item));
    }
  });
};

export const markStudyPlanItemCompleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(api.markStudyPlanItemComplete, {
    onMutate: async (item) => {
      await queryClient.cancelQueries(['completedStudyPlanItems', 'presetStudyPlanItems', 'studyPlanChunks']);

      const completedItem = { ...item, completedAt: moment().utc().format('YYYY-MM-DDThh:mm:ss.SSSZ') };
      const { planType } = queryClient.getQueryData('studyPlan') as StudyPlanDataInterface;

      if (planType === 'adaptive') {
        // Optimistically move the item to the completed tab
        const currentCompletedItems: StudyPlanItem[] = queryClient.getQueryData('completedStudyPlanItems');
        queryClient.setQueryData('completedStudyPlanItems', [...currentCompletedItems, completedItem]);

        // Optimistically remove the item from the todo tab
        const currentChunks: Chunk[] = queryClient.getQueryData('studyPlanChunks');
        queryClient.setQueryData('studyPlanChunks', chunksWithoutItem(currentChunks, item));
      } else {
        const presetItems: StudyPlanItem[] = queryClient.getQueryData('presetStudyPlanItems');
        const itemIndex = findIndex(presetItems, (item) => item.id === completedItem.id);
        presetItems[itemIndex] = completedItem;
        queryClient.setQueryData('presetStudyPlanItems', presetItems);
      }
    }
  });
};

export const setAdaptiveHoursPerChunkMutation = () => {
  const queryClient = useQueryClient();

  return useMutation((hours: number) => api.updateStudyPlan({ adaptiveHoursPerChunk: hours }), {
    onMutate: async (hours: number) => {
      await queryClient.cancelQueries(['studyPlan', 'studyPlanChunks']);

      const currentData: any = queryClient.getQueryData('studyPlan');
      queryClient.setQueryData('studyPlan', { ...currentData, adaptiveHoursPerChunk: hours });
    },
    onSuccess: () => {
      queryClient.invalidateQueries('studyPlan');
      queryClient.resetQueries('studyPlanChunks');
    }
  });
};

export const updateStudyPlanMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(api.updateStudyPlan, {
    onMutate: async () => {
      await queryClient.cancelQueries([
        'studyPlan',
        'completedStudyPlanItems',
        'presetStudyPlanItems',
        'studyPlanChunks'
      ]);
      return {};
    },
    onSuccess: (result: any, variables, context) => {
      queryClient.setQueryData('studyPlan', result.studyPlan);
      queryClient.invalidateQueries('completedStudyPlanItems');

      // Using `resetQueries` here so that the UI will go back into the loading state
      if (result.studyPlan.planType == 'adaptive') {
        queryClient.resetQueries('studyPlanChunks');
      } else {
        queryClient.resetQueries('presetStudyPlanItems');
      }
    }
  });
};
